import React, { useState, useEffect } from "react";
import { FormControlLabel, Switch, Checkbox } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

// Card no padrão dos toggles de "Recursos" (mockup Configurações A):
// label + descrição + switch em gradiente, com os perfis logo abaixo.
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    sectionLabel: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      margin: "24px 0 12px",
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 12,
      padding: "13px 18px",
      minWidth: 0,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 14,
    },
    body: { flex: 1, minWidth: 0 },
    label: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    desc: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
      lineHeight: 1.35,
    },
    toggleSwitch: {
      flexShrink: 0,
      width: 36,
      height: 20,
      padding: 0,
      "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": { transform: "translateX(16px)" },
      },
      "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
      "& .MuiSwitch-track": { borderRadius: 99, opacity: 1 },
    },
    profiles: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginTop: 8,
      paddingTop: 8,
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    profileOption: {
      margin: 0,
      "& .MuiFormControlLabel-label": { fontSize: 12.5 },
      "& .MuiCheckbox-root": { padding: 4 },
    },
  };
});

const PROFILE_OPTIONS = ["admin", "user"];

const OnboardingAssistantSettings = () => {
  const classes = useStyles();
  const { getAll, update } = useSettings();

  const [enabled, setEnabled] = useState(false);
  const [profiles, setProfiles] = useState(["admin"]);

  useEffect(() => {
    (async () => {
      try {
        const settings = await getAll();
        const findValue = (k) => {
          const s = (settings || []).find((item) => item.key === k);
          return s ? s.value : undefined;
        };
        setEnabled(findValue("onboardingAssistantEnabled") === "enabled");
        setProfiles(
          (findValue("onboardingAssistantProfiles") || "admin")
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean)
        );
      } catch (e) {
        // mantem defaults
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = async (nextEnabled, nextProfiles) => {
    try {
      await update({
        key: "onboardingAssistantEnabled",
        value: nextEnabled ? "enabled" : "disabled",
      });
      await update({
        key: "onboardingAssistantProfiles",
        value: nextProfiles.join(","),
      });
      toast.success("Configuracao do assistente salva.");
    } catch (e) {
      toast.error("Nao foi possivel salvar a configuracao.");
    }
  };

  const handleToggleEnabled = (e) => {
    const next = e.target.checked;
    setEnabled(next);
    persist(next, profiles);
  };

  const handleToggleProfile = (profile) => {
    const next = profiles.includes(profile)
      ? profiles.filter((p) => p !== profile)
      : [...profiles, profile];
    setProfiles(next);
    persist(enabled, next);
  };

  const title = i18n.t("settings.settings.sections.onboarding");

  return (
    <>
      <div className={classes.sectionLabel}>{title}</div>
      <div className={classes.grid2}>
        <div className={classes.card}>
          <div className={classes.row}>
            <div className={classes.body}>
              <div className={classes.label}>{title}</div>
              <div className={classes.desc}>
                {i18n.t("settings.settings.descriptions.onboarding")}
              </div>
            </div>
            <Switch
              className={classes.toggleSwitch}
              checked={enabled}
              onChange={handleToggleEnabled}
              inputProps={{ "aria-label": title }}
            />
          </div>
          <div className={classes.profiles}>
            <span>{i18n.t("settings.settings.descriptions.onboardingProfiles")}:</span>
            {PROFILE_OPTIONS.map((profile) => (
              <FormControlLabel
                key={profile}
                className={classes.profileOption}
                control={
                  <Checkbox
                    color="primary"
                    checked={profiles.includes(profile)}
                    onChange={() => handleToggleProfile(profile)}
                  />
                }
                label={profile}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingAssistantSettings;
